/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FC, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'

import { useDispatch } from 'react-redux'
import { password } from '../../utils/validate'
import { URL } from '../constants/url'
import { flatten } from 'lodash'

import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useAlert } from 'react-alert'
import { updatePasswordWithToken } from '../../states/containers/auth/resetPasswordSlice'
import { getGetPrams } from '../../utils/url'

const ResetPassword: FC = () => {
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const alert = useAlert()
  const [initLang, setInitLang] = useState('ja')

  const [passwordErrors, setPasswordErrors] = useState<string[] | undefined>()
  const [confirmationPasswordError, setConfirmationPasswordError] = useState<string[] | undefined>()
  const [isPasswordLoading, setIsPasswordLoading] = useState<boolean>(false)
  const passwordRef = useRef<HTMLInputElement>(null)
  const confirmationPasswordRef = useRef<HTMLInputElement>(null)
  const [token, setToken] = useState<string | undefined>(undefined)

  interface GetParams {
    token: string
  }

  const matchPassword = (value: string) => {
    if (!value) {
      setConfirmationPasswordError(undefined)
      return false
    }
    if (passwordRef.current?.value === value) {
      setConfirmationPasswordError(undefined)
      if (passwordErrors) {
        return false
      }
      return true
    }
    setConfirmationPasswordError([t('login.reset_password.error.passwords_do_not_match')])
    return false
  }

  const validatePassword = (value: string) => {
    const confirmationPassword = confirmationPasswordRef.current?.value
    if (confirmationPassword) {
      matchPassword(confirmationPassword)
    }
    if (!value) {
      setPasswordErrors(undefined)
      return false
    }
    const errors = password(value, {})
    if (errors) {
      setPasswordErrors(errors)
      return false
    }
    setPasswordErrors(undefined)
    return true
  }

  const handlePasswordSubmit = () => {
    if (!token || !passwordRef.current?.value || !confirmationPasswordRef.current?.value) return
    if (!validatePassword(passwordRef.current?.value)) return
    if (!matchPassword(confirmationPasswordRef.current?.value)) return
    setIsPasswordLoading(true)

    const params = {
      token: token,
      password: passwordRef.current?.value,
      password_confirmation: confirmationPasswordRef.current?.value,
    }
    dispatch(updatePasswordWithToken(
      params,
      () => {
        alert.success(t('form.alert.password_updated'))
        history.push(`${URL.LOGIN.path}`)
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (errors: any[]) => {
        const tmpErrors = flatten(errors.map((error) => error.message))
        setPasswordErrors(tmpErrors.map((error) => t(`validate${error}`)))
        setIsPasswordLoading(false)
      },
    ))
  }

  useEffect(() => {
    let browserLangValue = (navigator.languages && navigator.languages[0]) || navigator.language
    if (browserLangValue.match(/en/)) {
      browserLangValue = 'en'
    } else {
      browserLangValue = 'ja'
    }
    setInitLang(browserLangValue)
    const getParams = getGetPrams(location.search) as GetParams
    if (!getParams.token) {
      history.push(`${URL.LOGIN.path}`)
    }
    setToken(getParams.token)
  }, [])

  useEffect(() => {
    i18n.changeLanguage(initLang)
  }, [initLang, i18n])

  return (
    <>
      <Helmet>
        <title>Qasee - resetPassword</title>
      </Helmet>

      <div className="single_wrapper">
        <div className="login-logo">
          <img
            className="login-logo"
            src="/static/images/logo-login.png"
            alt="Logo login"
          />
        </div>
        <div className="sign_in_title">
          {t('login.reset_password.title')}
        </div>
        <div className="login-box-body">
          <div className="password_reset">
            <div className="input-area">
              <p>
                {t('login.reset_password.password')}
              </p>
              <input
                type="password"
                placeholder={t('login.reset_password.placeholder')}
                onChange={(e) => validatePassword(e.target.value)}
                className={passwordErrors && "error"}
                ref={passwordRef}
                disabled={isPasswordLoading}
              />
              {
                passwordErrors && passwordErrors.map((error: string) => (
                  <React.Fragment key={error}>
                    <div className="password_error">{t(`${error}`)}</div>
                  </React.Fragment>
                ))
              }
            </div>
            <div className="input-area">
              <p>
                {t('login.reset_password.confirmation_password')}
              </p>
              <input
                type="password"
                placeholder={t('login.reset_password.placeholder')}
                onChange={(e) => matchPassword(e.target.value)}
                className={confirmationPasswordError && "error"}
                ref={confirmationPasswordRef}
                disabled={isPasswordLoading}
              />
              {
                confirmationPasswordError && (
                  <div className="password_error">{confirmationPasswordError}</div>
                )
              }
            </div>
            <div className="button-area">
              <button
                type="button"
                className="btn btn-primary"
                disabled={isPasswordLoading}
                onClick={() => handlePasswordSubmit()}
              >
                {
                  isPasswordLoading ? (
                    <div className="loading" />
                  ) : (
                    t('login.reset_password.button.text')
                  )
                }
              </button>
            </div>
            {/* eslint-disable-next-line react/no-danger */}
            <div className="supplement" dangerouslySetInnerHTML={{ __html: t('login.reset_password.supplement') }} />
          </div>
        </div>
      </div>
    </>
  )
}

export default ResetPassword
